import type { ReferralModel } from '@ctw/shared/api/fhir/models/referral';
import { Badge } from '@ctw/shared/components/badge';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import type { LegacyTableColumn } from '@ctw/shared/components/tables/legacy-table';
import { EmptyPatientTable } from '@ctw/shared/content/empty-patients-table';
import { ResourceTable } from '@ctw/shared/content/resource/resource-table';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import { usePatientReferrals } from '@ctw/shared/hooks/use-patient-referrals';
import { tw } from '@ctw/shared/utils/tailwind';
import { faFileLines } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { capitalize } from 'lodash-es';

export const PatientReferrals = withErrorBoundary({
  boundaryName: 'PatientReferrals',
  Component: () => {
    const { requestContext } = useCTW();
    const referralsQuery = usePatientReferrals();
    const { openResourceDrawer } = useResourceDrawer();

    return (
      <ResourceTable
        title="Referrals"
        titleVisuallyHidden={true}
        showTableHead={true}
        isLoading={referralsQuery.isLoading}
        data={referralsQuery.data ?? []}
        stackedBreakpoint="xs"
        columns={patientReferralsColumns(requestContext.builderId)}
        onRowClick={({ telemetry, record }) => openResourceDrawer({ telemetry, model: record })}
        enableDismissAndReadActions={true}
        emptyMessage={<EmptyPatientTable hasZeroFilteredRecords={false} resourceName="referrals" />}
      />
    );
  },
});

const patientReferralsColumns = (_builderId: string) => {
  const referralColumns: Array<LegacyTableColumn<ReferralModel>> = [
    {
      title: 'Reason',
      render: (referral) => <div className={tw`leading-tight`}>{referral.reason}</div>,
    },
    {
      title: 'Referred On',
      render: (referral) => (
        <div className={tw`leading-tight`}>
          <span className={tw`whitespace-nowrap`}>{referral.referredAtDisplay ?? ''}</span>
          {referral.referringPractitionerName && (
            <>
              <br />
              <span className={tw`text-xs`}>by {referral.referringPractitionerName}</span>
            </>
          )}
        </div>
      ),
    },
    {
      title: 'Referred Provider',
      render: (referral) => (
        <div className={tw`leading-tight`}>
          <span>{referral.referredPractitionerName ?? ''}</span>
          {referral.referredPracticeName && (
            <>
              <br />
              <span>{referral.referredPracticeName}</span>
            </>
          )}
          {referral.referredPractitioners.length > 1 && (
            <>
              <br />
              <span className={tw`text-xs`}>
                + {referral.referredPractitioners.length - 1} More
              </span>
            </>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      render: (referral) => (
        <div className={tw`flex flex-col gap-1`}>
          <div className={tw`flex w-full items-center justify-between`}>
            <Badge
              variant={referral.status === 'fulfilled' ? 'meta' : 'primary'}
              content={capitalize(referral.status)}
            />
            {referral.getIsLoadingDocuments() && <LoadingSpinner className={tw`h-4 w-4`} />}
            {referral.encounter?.docsAndNotes && referral.encounter.docsAndNotes.length === 0 && (
              <FontAwesomeIcon className={tw`w-4 text-content-icon`} icon={faFileLines} />
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Referral Details',
      render: (referral) => (
        <div className={tw`flex flex-col gap-1`}>
          {['ordered', 'scheduled', 'fulfilled'].includes(referral.status) && (
            <div className={tw`leading-tight`}>
              {referral.referredAtDisplay && (
                <>
                  <span className={tw`whitespace-nowrap text-sm`}>
                    Referred {referral.referredAtDisplay}
                  </span>
                  {referral.referringPracticeName && (
                    <>
                      <br />
                      <span className={tw`text-content-light text-xs`}>
                        by {referral.referringPracticeName}
                      </span>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {['scheduled', 'fulfilled'].includes(referral.status) && (
            <div className={tw`leading-tight`}>
              {referral.scheduledAtDisplay && (
                <>
                  <span className={tw`whitespace-nowrap text-sm`}>
                    Scheduled {referral.scheduledAtDisplay}
                  </span>
                  {referral.referredPracticeName && (
                    <>
                      <br />
                      <span className={tw`text-content-light text-xs`}>
                        by {referral.referredPracticeName}
                      </span>
                    </>
                  )}
                </>
              )}
            </div>
          )}
          {['fulfilled'].includes(referral.status) && (
            <div className={tw`leading-tight`}>
              {referral.completedAtDisplay && (
                <>
                  <span className={tw`whitespace-nowrap`}>
                    Completed {referral.completedAtDisplay}
                  </span>
                  {referral.referredPracticeName && (
                    <>
                      <br />
                      <span className={tw`text-content-light text-xs`}>
                        by {referral.referredPracticeName}
                      </span>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      ),
    },
  ];

  return referralColumns;
};
